import { AssertNotNull, cast, castOrNull, ConditionalLog } from "./tools";
import { createPopper, Instance, Modifier } from '@popperjs/core';
import { ProgCss } from "./ProgCss.Generated";
import { focusableChoicesWithin } from "./collectFuzzyFocusSelector";
import { wireUpProgressJsControlsWithin } from "./wireUpProgressJsControlsWithin";

const tooltipCssClassClickable = `.${ProgCss.Tooltip_link}.${ProgCss.is_shownOnClick}`;
const tooltipCssClassHover = `.${ProgCss.Tooltip_link}.${ProgCss.is_shownOnHover}`;

const tooltipInfoKey = Symbol("tooltipInfoCache");
const tooltipTargetKey = Symbol("tooltipTargetCache");
declare global { interface Element { [tooltipInfoKey]: Instance | undefined, [tooltipTargetKey]: Instance | undefined } }

let openInitiallyOpen: ((wireupScope: HTMLElement) => void) | null = null;

export function GetOpenedClickableTooltipsCount(scope: HTMLElement) {
    return scope.querySelectorAll(`.${ProgCss.Tooltip}.${ProgCss.is_shown}.${ProgCss.is_unboundInSize}`).length;
}

export function StartTooltipsModule() {
    if (openInitiallyOpen)
        throw new Error("tooltips already initialized");

    openInitiallyOpen = (wireupScope) => {
        for (const maybeElWithTooltip of wireupScope.querySelectorAll(tooltipCssClassClickable + "[data-popover-initially-open]")) {
            const clickableTooltip = getTooltipInfoFromTarget(maybeElWithTooltip, tooltipCssClassClickable, postInitClickablePopOver);
            if (clickableTooltip) {
                displayClickableTooltip(clickableTooltip);
            }
        }
    };

    (window as any).GetHoverTooltipFromTarget_ForTest = (target: EventTarget | null): Element | undefined => {
        return getTooltipInfoFromTarget(target, tooltipCssClassHover, postInitHoverablePopOver)?.state.elements.popper;
    }

        (window as any).GetClickableTooltipFromTarget_ForTest = (target: EventTarget | null): Element | undefined => {
        return getTooltipInfoFromTarget(target, tooltipCssClassClickable, postInitHoverablePopOver)?.state.elements.popper;
    }

    document.addEventListener("mouseover", e => {
        let target = e.target;
        while (target) {
            const tooltip = getTooltipInfoFromTarget(target, tooltipCssClassHover, postInitHoverablePopOver);
            if (!tooltip)
                break;
            displayHoverableTooltip(tooltip);
            target = cast(tooltip.state.elements.reference, Element).parentElement;
        }
    });

    (window as any).GetClickableTooltipFromTarget_ForTest = (target: EventTarget | null): HTMLElement | undefined => {
        return getTooltipInfoFromTarget(target, tooltipCssClassClickable, postInitClickablePopOver)?.state.elements.popper;
    };

    document.addEventListener("click", e => {
        const tooltip = getTooltipInfoFromTarget(e.target, tooltipCssClassClickable, postInitClickablePopOver);
        if (tooltip) {
            displayClickableTooltip(tooltip);
            e.preventDefault();
            e.stopPropagation(); //dubious, we don't want this, I think...
        }
    }, true);
}

export function openInitiallyOpenPopovers(wireupScope: HTMLElement) {
    if (!openInitiallyOpen)
        throw new Error("tooltips not yet initialized");
    openInitiallyOpen(wireupScope);
}

const hoverableTooltipOffsetModifier = { name: 'offset', options: { offset: [0, 8] } };
const clickableTooltipOffsetModifier = { name: 'offset', options: { offset: [0, 8] } };
const preventOverflowModifier = { name: 'preventOverflow', options: { boundary: 'viewport', padding: 8 } };
const minWidthModifier: Modifier<'minWidth', any> = {
    name: 'minWidth',
    enabled: true,
    phase: 'beforeWrite',
    requires: ['computeStyles'],
    fn: () => { },
    effect: ({ state }) => {
        if (!state.elements.arrow) {
            const referenceElementWidth = state.elements.reference.getBoundingClientRect().width;
            if (referenceElementWidth < 300) {
                state.elements.popper.style.minWidth = `${state.elements.reference.getBoundingClientRect().width}px`;
            }
        }
    }
};

function displayHoverableTooltip(tooltip: Instance) {
    tooltip.state.elements.popper.classList.add(ProgCss.is_renderable); //cannot be transitioned, due to display none->something change

    tooltip.setOptions({
        placement: 'top',
        modifiers: [preventOverflowModifier, hoverableTooltipOffsetModifier],
    });

    tooltip.state.elements.popper.classList.add(ProgCss.is_shown); //causes transition-friendly visibility change
}

function displayClickableTooltip(tooltip: Instance) {
    tooltip.state.elements.popper.classList.add(ProgCss.is_renderable, ProgCss.is_shown, ProgCss.is_unboundInSize); //cannot be transitioned, due to display none->something change
    cast(tooltip.state.elements.reference, Element).classList.add(ProgCss.is_active);

    tooltip.setOptions({
        placement: tooltip.state.elements.arrow ? 'bottom' : 'bottom-start',
        modifiers: tooltip.state.elements.arrow ? [preventOverflowModifier, clickableTooltipOffsetModifier] : []
    });

    queueMicrotask(() => {
        //this must NOT be a full JS task, because otherwise having focus may be in race condition with the hyper-fast virtual input in smoketests!
        const target = tooltip.state.elements.popper;
        for (const possibility of focusableChoicesWithin(target)) {
            if (possibility !== target) {
                possibility.focus();
                ConditionalLog.log('Autofocusing within open-on-click popover', possibility);
                return;
            }
        }

        target.focus(); // has tabindex=-1
    });
}

function getTooltipInfoFromTarget(target: EventTarget | null, popoverKind: string, postInit: (tooltip: Instance) => Instance): Instance | null {
    const tooltipOrigin = castOrNull(target, Element)?.closest(popoverKind);
    if (!tooltipOrigin) {
        return null;
    }

    return tooltipOrigin[tooltipInfoKey] || postInit(createTooltipInstance(tooltipOrigin));
}

function createTooltipInstance(tooltipOrigin: Element): Instance {
    const pnetForm = document.getElementById("formulier");
    const popoverHostEl = pnetForm instanceof HTMLFormElement ? pnetForm : document.body;

    const shouldShowArrow = tooltipOrigin.hasAttribute('data-popper-showarrow');
    const tooltipWrapper = document.createElement("div");
    tooltipWrapper.classList.add(ProgCss.Tooltip);
    if (shouldShowArrow) {
        tooltipWrapper.classList.add(ProgCss.u_roundedCornersSmall);
        const popperArrow = document.createElement("div");
        popperArrow.setAttribute('data-popper-arrow', '');
        tooltipWrapper.append(popperArrow);
    }
    const tooltipContent = document.createElement("div");
    tooltipContent.classList.add(ProgCss.Tooltip_content);

    const popoverHtml = tooltipOrigin.getAttribute('data-popover-html');
    const popoverText = tooltipOrigin.getAttribute('data-popover-text');
    if (popoverText !== null) {
        tooltipContent.textContent = popoverText;
    } else if (popoverHtml !== null) {
        tooltipContent.innerHTML = popoverHtml;
    } else {
        for (const possibleTooltip of tooltipOrigin.childNodes) {
            if (possibleTooltip instanceof HTMLTemplateElement && possibleTooltip.classList.contains(ProgCss.Tooltip)) {
                tooltipContent.append(possibleTooltip.content);
                possibleTooltip.remove();
                break;
            }
        }
    }
    tooltipWrapper.append(tooltipContent);

    popoverHostEl.appendChild(tooltipWrapper);
    hackToSuppressLastPassConsoleErrorMessages(tooltipWrapper);

    wireUpProgressJsControlsWithin(tooltipWrapper);
    return tooltipWrapper[tooltipTargetKey] = tooltipOrigin[tooltipInfoKey] =
        createPopper(tooltipOrigin, tooltipWrapper, { modifiers: [minWidthModifier], });
}

export function getTooltipTargetFromWrapper(tooltipWrapper: HTMLElement): Element | null {
    const popover = tooltipWrapper[tooltipTargetKey];
    return popover ? cast(popover.state.elements.reference, Element) : null;
}

function postInitHoverablePopOver(popover: Instance): Instance {
    const tooltipOrigin = cast(popover.state.elements.reference, Element);
    const tooltipWrapper = popover.state.elements.popper;
    tooltipWrapper.classList.add(ProgCss.Tooltip__openOnHover);

    tooltipOrigin.addEventListener("mouseleave", () => {
        tooltipWrapper.classList.remove(ProgCss.is_shown);
        popover.setOptions({ modifiers: [hoverableTooltipOffsetModifier, { name: 'eventListeners', enabled: false }], });
    });

    return popover;
}

function postInitClickablePopOver(popover: Instance): Instance {
    const tooltipWrapper = popover.state.elements.popper;
    tooltipWrapper.tabIndex = -1;
    const close = () => {
        var tooltipRef = cast(popover.state.elements.reference, Element);
        if (tooltipRef.classList.contains(ProgCss.is_active)) {
            tooltipRef.classList.remove(ProgCss.is_active);
            tooltipWrapper.classList.remove(ProgCss.is_renderable, ProgCss.is_shown);
            popover.setOptions({ modifiers: [clickableTooltipOffsetModifier, { name: 'eventListeners', enabled: false }], });
            var element = cast(popover.state.elements.reference, Element);
            var closeCallback = element.getAttribute("data-close-callback");

            if (closeCallback) {
                const inputEl = document.createElement("input");
                inputEl.type = "hidden";
                inputEl.name = closeCallback;

                const form = AssertNotNull(element.closest("form"));
                form.append(inputEl);
                form.requestSubmit();
            }
        }
    };

    var closeButtons = tooltipWrapper.querySelectorAll("[data-close-tooltip]");
    for (const button of closeButtons) {
        button.addEventListener("click", close);
    }

    tooltipWrapper.addEventListener("keyup", evt => {
        if (evt.key == "Escape") {
            close();
            evt.preventDefault();
        }
    });

    tooltipWrapper.addEventListener("focusout",
        evt => {
            const gotFocus = evt.relatedTarget ?? document.activeElement;
            if (!gotFocus || gotFocus instanceof Node && !tooltipWrapper.contains(gotFocus)
                && tooltipWrapper !== gotFocus
                && popover.state.elements.reference !== gotFocus) {
                close();
            }
        }
    );
    return popover;
}

function hackToSuppressLastPassConsoleErrorMessages(tooltipWrapper: HTMLElement) {
    tooltipWrapper.addEventListener("keydown", e => {
        if (e.key === "Enter" && e.target instanceof HTMLTextAreaElement) {
            e.stopPropagation();
        }
    });
}

export function addTooltipToTarget(target: Element, tooltipContent: (Node | string)[], isUndashed: boolean, openOnClick: boolean) {
    const tooltipTemplate = document.createElement("template");
    tooltipTemplate.classList.add(ProgCss.Tooltip);
    tooltipTemplate.content.append(...tooltipContent);
    target.classList.add(ProgCss.Tooltip_link);

    if (openOnClick) {
        target.classList.add(ProgCss.is_shownOnClick);
    } else {
        target.classList.add(ProgCss.is_shownOnHover);
        target.setAttribute("data-popper-showarrow", "");
    }

    if (isUndashed) {
        target.classList.add(ProgCss.undashed);
    }

    target.appendChild(tooltipTemplate);
}
